//frontend/src/components/Auth/ResetPassword.jsx


import React, { useState } from 'react';
import { Container, Card, Form, Button, Alert, InputGroup, ProgressBar } from 'react-bootstrap';
import { FaLock, FaEye, FaEyeSlash, FaCheckCircle } from 'react-icons/fa';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../../services/api';
import { toast } from 'react-toastify';
import './Auth.css';

const ResetPassword = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        new_password: '',
        confirm_password: ''
    });
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [passwordStrength, setPasswordStrength] = useState(0);

    const calculatePasswordStrength = (password) => {
        let strength = 0;
        if (password.length >= 8) strength += 25;
        if (password.match(/[a-z]+/)) strength += 25;
        if (password.match(/[A-Z]+/)) strength += 25;
        if (password.match(/[0-9]+/)) strength += 25;
        return strength;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (name === 'new_password') {
            setPasswordStrength(calculatePasswordStrength(value));
        }
    };

    const getPasswordStrengthColor = () => {
        if (passwordStrength < 50) return 'danger';
        if (passwordStrength < 75) return 'warning';
        return 'success';
    };

    const getPasswordStrengthText = () => {
        if (passwordStrength < 50) return 'Faible';
        if (passwordStrength < 75) return 'Moyen';
        return 'Fort';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (formData.new_password !== formData.confirm_password) {
            setError('Les mots de passe ne correspondent pas');
            toast.error('❌ Les mots de passe ne correspondent pas');
            return;
        }

        if (passwordStrength < 75) {
            setError('Le mot de passe doit être plus fort');
            toast.warning('⚠️ Mot de passe trop faible');
            return;
        }

        setLoading(true);

        try {
            await api.post('/users/password-reset-confirm/', {
                token,
                new_password: formData.new_password,
                confirm_password: formData.confirm_password
            });
            setSuccess(true);
            toast.success('✅ Mot de passe réinitialisé avec succès !');
            setTimeout(() => navigate('/login'), 3000);
        } catch (error) {
            const errorMsg = error.response?.data?.error || 
                            error.response?.data?.message || 
                            'Erreur lors de la réinitialisation';
            setError(errorMsg);
            toast.error('❌ ' + errorMsg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="auth-page">
            <Container className="py-5">
                <Card className="shadow-lg" style={{ maxWidth: '550px', margin: '0 auto' }}>
                    <Card.Body className="p-5">
                        <div className="text-center mb-4">
                            <div className="mb-3">
                                <FaLock size={60} className="text-primary" />
                            </div>
                            <h2 className="mb-2">Nouveau mot de passe</h2>
                            <p className="text-muted">
                                Choisissez un mot de passe fort et sécurisé
                            </p>
                        </div>

                        {error && (
                            <Alert variant="danger" className="mb-4">
                                {error}
                            </Alert>
                        )}

                        {success ? (
                            <Alert variant="success" className="text-center">
                                <FaCheckCircle size={60} className="text-success mb-3" />
                                <h4 className="mb-3">Mot de passe réinitialisé !</h4>
                                <p className="mb-3">
                                    Votre mot de passe a été modifié avec succès.
                                </p>
                                <p className="text-muted mb-3">
                                    Vous allez être redirigé vers la page de connexion...
                                </p>
                                <hr />
                                <Link to="/login" className="btn btn-primary">
                                    Se connecter maintenant
                                </Link>
                            </Alert>
                        ) : (
                            <Form onSubmit={handleSubmit}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Nouveau mot de passe</Form.Label>
                                    <InputGroup>
                                        <InputGroup.Text>
                                            <FaLock />
                                        </InputGroup.Text>
                                        <Form.Control
                                            type={showPassword ? "text" : "password"}
                                            name="new_password"
                                            value={formData.new_password}
                                            onChange={handleChange}
                                            required
                                            placeholder="Min 8 caractères"
                                            size="lg"
                                        />
                                        <InputGroup.Text 
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => setShowPassword(!showPassword)}
                                        >
                                            {showPassword ? <FaEyeSlash /> : <FaEye />}
                                        </InputGroup.Text>
                                    </InputGroup>
                                    {formData.new_password && (
                                        <div className="mt-2">
                                            <ProgressBar 
                                                now={passwordStrength} 
                                                variant={getPasswordStrengthColor()}
                                                style={{height: '6px'}}
                                            />
                                            <small className={`text-${getPasswordStrengthColor()}`}>
                                                Force: {getPasswordStrengthText()}
                                            </small>
                                        </div>
                                    )}
                                </Form.Group>

                                <Form.Group className="mb-4">
                                    <Form.Label>Confirmer le mot de passe</Form.Label>
                                    <InputGroup>
                                        <InputGroup.Text>
                                            <FaLock />
                                        </InputGroup.Text>
                                        <Form.Control
                                            type={showConfirmPassword ? "text" : "password"}
                                            name="confirm_password"
                                            value={formData.confirm_password}
                                            onChange={handleChange}
                                            required
                                            placeholder="Confirmez votre mot de passe"
                                            size="lg"
                                        />
                                        <InputGroup.Text 
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        >
                                            {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                                        </InputGroup.Text>
                                    </InputGroup>
                                </Form.Group>

                                <Button 
                                    type="submit" 
                                    variant="primary" 
                                    className="w-100"
                                    size="lg"
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2"></span>
                                            Réinitialisation...
                                        </>
                                    ) : (
                                        'Réinitialiser le mot de passe'
                                    )}
                                </Button>
                            </Form>
                        )}

                        <hr className="my-4" />

                        <div className="text-center">
                            <Link to="/login" className="text-primary">
                                ← Retour à la connexion
                            </Link>
                        </div>
                    </Card.Body>
                </Card>
            </Container>
        </div>
    );
};

export default ResetPassword;